import * as db_imported from './chats.store.js';
import { uuid } from '../../application_element/lib/utils.js';
import { csrfFetch } from '../../../lib/helpers.js';
import ServerTime from '../../../lib/server_time.js';
import { compactObject } from '../../../lib/helpers.js';

let db = db_imported; // for testing with sinon

export class ChatModel {
  static fetchCache = {};

  static clearUserData() {
    db.currentUserStore.set({});
    db.store.set({ chats: new Map() });

    this.fetchCache = {};
  }

  static get length() {
    return db.store.value.chats.size;
  }

  // for testing with sinon
  static setDb(newDb) {
    db = newDb;
  }

  static applyPatches(patches) {
    db.applyPatchesToStore(patches);
  }

  static setActive(id) {
    return db.setActive(id);
  }

  static get activeChat() {
    return db.getChat(db.store.value.activeChatId);
  }

  static loadChats(url, { cache, ...query }) {
    const cacheKey = JSON.stringify({ url, query });

    const { fetchCache } = this;
    const cachedRequest = fetchCache[cacheKey];

    if (cachedRequest) return cachedRequest;
    const request = db.loadChats({ url, query: compactObject(query) });

    if (cache) fetchCache[cacheKey] ||= request;

    return request;
  }

  constructor(id) {
    this.id = String(id);
  }

  update(attributes) {
    db.updateChat({ chatId: this.id, ...attributes });
  }

  get messages() {
    return this.storedChat.messages;
  }

  get muted() {
    return this.storedChat.isMuted;
  }

  // TODO: adjust for story book /simulate/chats
  get chatUrl() {
    return `/chats/${this.id}/messages`;
  }

  get storedChat() {
    return db.getChat(this.id);
  }

  get signatureUrl() {
    return this.chatUrl.replace('messages', 'signature');
  }

  get mainChatUrl() {
    return this.chatUrl.replace('/messages', '');
  }

  get toggleMuteUrl() {
    return this.chatUrl.replace('messages', 'toggle_mute');
  }

  get markAllAsReadUrl() {
    return this.chatUrl.replace('messages', 'mark_all_as_read');
  }

  message(id) {
    return this.messages.get(id);
  }

  remove() {
    db.removeChat({ chatId: this.id });
    delete this.constructor.fetchCache[this.id];
  }

  async toggleMute() {
    const originalState = this.storedChat.isMuted;
    this.update({ chatId: this.id, isMuted: !originalState });

    try {
      await csrfFetch(this.toggleMuteUrl, {
        method: 'put',
      });
    } catch (error) {
      this.update({ chatId: this.id, isMuted: originalState });
    }
  }

  async exit() {
    try {
      await csrfFetch(this.mainChatUrl, {
        method: 'delete',
      });
      this.remove();
    } catch (error) {
      // this.destroy();
    }
  }

  async fetchCurrentUserSignature() {
    const response = await csrfFetch(this.signatureUrl);
    const { signature } = await response.json();
    return signature;
  }

  load(callback) {
    this.constructor.fetchCache[this.id] ||= db.loadChat(
      this.mainChatUrl,
      callback
    );

    return this.constructor.fetchCache[this.id];
  }

  initialize(attributes) {
    db.initialize({ chatId: this.id, ...attributes });
  }

  newMessage({ author, file, ...attributes }) {
    const item = {
      file,
      uuid: uuid(),
      createdAt: ServerTime.toJSON(),
      timeSyncedAt: ServerTime.timeSyncedAtJSON,
      body: null,
      attachment: file
        ? {
            type: file.type,
            size: file.size,
            original_filename: file.name,
          }
        : null,
      uploading: !!file,
      userId: author.id,
      userName: author.name,
      userPicture: author.picture,
      isUserOfficial: author.isRepresentative,
      readIds: [author.id],
      ...attributes,
    };

    return item;
  }

  upsertMessages(messages) {
    return db.upsertMessages({ chatId: this.id, messages });
  }

  getMessage(uuid) {
    return db.getMessage({ chatId: this.id, uuid });
  }

  insertMessage(message) {
    return db.insertMessage({ chatId: this.id, message });
  }

  updateMessage(message) {
    return db.updateMessage({ chatId: this.id, ...message });
  }

  removeMessage(uuid) {
    return db.removeMessage({ chatId: this.id, uuid });
  }

  loadMessages(params) {
    return db.loadMessages({ chatId: this.id, ...params });
  }

  loadMessage(params) {
    return db.loadMessage({ chatId: this.id, ...params });
  }

  markAllAsReadByUser({ userId, unreadMessagesIds }) {
    return db.markAllAsReadByUser({
      chatId: this.id,
      userId,
      unreadMessagesIds,
    });
  }

  markChatAsRead() {
    return db.markChatAsRead({
      url: this.markAllAsReadUrl,
      chatId: this.id,
    });
  }
}

export default ChatModel;

// TODO: This should be moved to rails projects
document.addEventListener('app:config:updated', e => {
  const { user } = e.detail;

  // Reset store on logout
  if (!user.id) {
    ChatModel.clearUserData();
  } else {
    db.currentUserStore.set(user);
  }
});
